// ============================================================
// Timeline.jsx — trim control + playhead scrubber (v1)
// ------------------------------------------------------------
// Drag the left/right handles to set the trim in/out points. Click the track
// to move the playhead (drives the preview + overlay visibility). Times are
// in seconds against the clip's measured duration.
// ============================================================

import React, { useCallback, useRef } from 'react';
import { fmtTime } from '../../utils/mockVideoAssets';

export default function Timeline({
  duration = 0,
  trimStart = 0,
  trimEnd = 0,
  playhead = 0,
  overlays = [],
  onTrimChange,
  onSeek,
  onDeleteClip,
}) {
  const trackRef = useRef(null);
  const dragRef  = useRef(null); // 'start' | 'end' | 'playhead' | null

  const pct = (t) => (duration ? Math.max(0, Math.min(100, (t / duration) * 100)) : 0);

  const posToTime = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el || !duration) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const onPointerMove = useCallback((e) => {
    const mode = dragRef.current;
    if (!mode) return;
    const t = posToTime(e.clientX);
    if (mode === 'start') {
      onTrimChange?.({ start: Math.min(t, trimEnd - 0.1) });
    } else if (mode === 'end') {
      onTrimChange?.({ end: Math.max(t, trimStart + 0.1) });
    } else if (mode === 'playhead') {
      onSeek?.(t);
    }
  }, [posToTime, onTrimChange, onSeek, trimStart, trimEnd]);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
  }, [onPointerMove]);

  const startDrag = (mode) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = mode;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  };

  const onTrackClick = (e) => {
    if (dragRef.current) return;
    onSeek?.(posToTime(e.clientX));
  };

  return (
    <div className="ve-timeline">
      <div className="ve-tl-times">
        <span>In {fmtTime(trimStart)}</span>
        <span className="ve-tl-dur">{fmtTime(Math.max(0, trimEnd - trimStart))} selected</span>
        <span>Out {fmtTime(trimEnd)}</span>
        {onDeleteClip && (
          <button className="ve-tl-del" onClick={onDeleteClip} title="Remove clip">✕</button>
        )}
      </div>

      <div className="ve-tl-track" ref={trackRef} onPointerDown={onTrackClick}>
        {/* Clipped fill layer — rounded corners stay tidy, but the trim handles
            below live outside it so they're never clipped at the track edges. */}
        <div className="ve-tl-fills">
          {/* dimmed regions outside the trim window */}
          <div className="ve-tl-dim" style={{ left: 0, width: `${pct(trimStart)}%` }} />
          <div className="ve-tl-dim" style={{ left: `${pct(trimEnd)}%`, right: 0 }} />

          {/* selected region */}
          <div
            className="ve-tl-sel"
            style={{ left: `${pct(trimStart)}%`, width: `${pct(trimEnd) - pct(trimStart)}%` }}
          />

          {/* overlay markers */}
          {overlays.map((o) => (
            <div
              key={o.id}
              className="ve-tl-marker"
              title={o.text}
              style={{ left: `${pct(o.inTime)}%`, width: `${Math.max(1, pct(o.outTime) - pct(o.inTime))}%` }}
            />
          ))}
        </div>

        {/* trim handles */}
        <div className="ve-tl-handle start" style={{ left: `${pct(trimStart)}%` }} onPointerDown={startDrag('start')} />
        <div className="ve-tl-handle end"   style={{ left: `${pct(trimEnd)}%` }}   onPointerDown={startDrag('end')} />

        {/* playhead */}
        <div className="ve-tl-playhead" style={{ left: `${pct(playhead)}%` }} onPointerDown={startDrag('playhead')} />
      </div>
    </div>
  );
}
